import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { useGo } from '../components/Nav';
import { Button, Spinner } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { config } from '../lib/env';
import { startDesktopHandoff } from '../queries/device';

/**
 * The bounce that signs Slate for Mac, and Slate for Chrome, in.
 *
 * The app opens this page with a `state` it made up. If nobody is signed in
 * here, we go through login or signup and come straight back. Once someone
 * is, we ask the API for a code already approved for them and send the
 * browser to slate://auth with that code and the same state, which is the
 * app's cue to collect its token. The code is dead two minutes later and
 * spent after one use, so the URL is not worth keeping.
 *
 * The Chrome extension has no URL scheme. It opens this page in Chrome's own
 * sign-in window (chrome.identity) and passes `redirect`, the address Chrome
 * gave it for that window, which is always https://<extension id>.chromiumapp.org/.
 * Sending the code there closes the window and hands the code to the
 * extension. Only Slate's own extension ids are sent one: any extension has
 * such an address, and a page that redirected to whichever one was named in
 * the link would sign somebody else's extension in as you.
 *
 * Nothing leaves without a click. A signed-in person who lands here from a
 * link they did not mean to open, whatever it names as the app, sees who is
 * asking and decides.
 */
/**
 * Each build of the app registers its own scheme (local, dev, prod) so a
 * machine with all three sends the code to the one that asked. Anything not
 * on this list is treated as the real app.
 */
const SCHEMES = new Set(['slate', 'slate-dev', 'slate-local']);

/** Which app is asking, for the name it gets in the session list. */
const APP_NAMES: Record<string, string> = {
  mac: 'Slate for Mac',
  windows: 'Slate for Windows',
  chrome: 'Slate for Chrome',
};

/**
 * The extension's own redirect address, or null for anything else: not
 * https, not chromiumapp.org, or an extension id that is not ours (see
 * `chromeExtensionIds` in lib/env.ts). The query and fragment are dropped;
 * ours is added.
 */
function extensionRedirect(redirect: string | undefined): string | null {
  if (!redirect) return null;
  try {
    const url = new URL(redirect);
    const match = /^([a-p]{32})\.chromiumapp\.org$/.exec(url.hostname);
    if (url.protocol !== 'https:' || !match || !config.chromeExtensionIds.includes(match[1])) return null;
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

export function DesktopPage({
  state,
  signup,
  scheme,
  app,
  redirect,
}: {
  state: string;
  signup: boolean;
  scheme: string;
  app?: string;
  redirect?: string;
}) {
  const appScheme = SCHEMES.has(scheme) ? scheme : 'slate';
  const appName = APP_NAMES[app ?? 'mac'] ?? APP_NAMES.mac;
  const extension = app === 'chrome' ? extensionRedirect(redirect) : null;
  const isExtension = extension !== null;
  const { user, loading } = useAuth();
  const go = useGo();
  const [launched, setLaunched] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const here =
    `/desktop?state=${encodeURIComponent(state)}&scheme=${encodeURIComponent(appScheme)}` +
    `${app ? `&app=${encodeURIComponent(app)}` : ''}${extension ? `&redirect=${encodeURIComponent(extension)}` : ''}`;

  useEffect(() => {
    if (loading || user) return;
    go(`${signup ? '/signup' : '/login'}?next=${encodeURIComponent(here)}`, { replace: true });
  }, [go, here, loading, signup, user]);

  // The extension asked to be sent somewhere we will not send a code. Said
  // plainly rather than silently falling back to the Mac app's scheme, which
  // would launch the wrong thing for the person and hide the problem from us.
  const refused = app === 'chrome' && !isExtension;

  const handoff = () => {
    if (!user || busy || refused) return;
    setBusy(true);
    setError(null);
    startDesktopHandoff(appName)
      .then(({ deviceCode }) => {
        const query = `code=${encodeURIComponent(deviceCode)}&state=${encodeURIComponent(state)}`;
        const target = extension ? `${extension}?${query}` : `${appScheme}://auth?${query}`;
        track('Desktop Sign In Started', { intent: signup ? 'sign_up' : 'sign_in' });
        setLaunched(target);
        window.location.href = target;
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6 text-center">
            {refused ? (
              <>
                <h1 className="text-h3 text-chalk">That is not Slate for Chrome</h1>
                <p className="mt-2 text-body text-chalk-dim">
                  This page was asked to sign in an extension that is not ours, so nothing was sent. If you are
                  running a development build of the extension, its id needs adding to this environment.
                </p>
              </>
            ) : error ? (
              <>
                <h1 className="text-h3 text-chalk">{appName} could not be signed in</h1>
                <p className="mt-2 text-body text-chalk-dim">{error}</p>
                <button
                  type="button"
                  className="mt-4 text-body text-signal hover:underline"
                  onClick={() => setError(null)}
                >
                  Try again
                </button>
              </>
            ) : launched && isExtension ? (
              <>
                <h1 className="text-h3 text-chalk">Slate for Chrome is signed in</h1>
                <p className="mt-2 text-body text-chalk-dim">
                  This window closes by itself. If it is still here, close it and click the Slate button again.
                </p>
              </>
            ) : launched ? (
              <>
                <h1 className="text-h3 text-chalk">Launching Slate</h1>
                <p className="mt-2 text-body text-chalk-dim">
                  If you are not taken back to the app,{' '}
                  <a href={launched} className="text-signal hover:underline">
                    click here
                  </a>
                  .
                </p>
                <p className="mt-4 text-caption text-chalk-faint">
                  You can close this tab afterwards. Recordings will land in your library.
                </p>
              </>
            ) : !user ? (
              <div className="flex items-center justify-center gap-2.5 py-4 text-chalk-faint">
                <Spinner />
                <span className="text-body">Checking your session…</span>
              </div>
            ) : (
              <>
                <h1 className="text-h3 text-chalk">Sign in {appName}?</h1>
                <p className="mt-2 text-body text-chalk-dim">
                  {appName} is asking to sign in as <span className="text-chalk">{user.email}</span>. It will be able
                  to record and upload to your library. If you did not just ask it to, close this tab.
                </p>
                <Button variant="primary" size="lg" className="mt-5 w-full" busy={busy} onClick={handoff}>
                  Continue to {appName}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
